import { useTranslation } from 'react-i18next';
import { Monitor, Moon, Palette, Sun, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { useThemeStore } from '@/stores/theme.store';
import { useAppearanceStore } from '@/stores/appearance.store';
import { ACCENTS, ACCENT_SWATCH, DENSITIES, FONT_SIZES, type AccentName, type ThemeChoice } from '@/lib/appearance';

const THEME_ICONS: Record<ThemeChoice, typeof Sun> = { light: Sun, dark: Moon, system: Monitor };
const THEME_KEYS: Record<ThemeChoice, string> = { light: 'lightTheme', dark: 'darkTheme', system: 'systemTheme' };
const FONT_KEYS = { small: 'textSizeSmall', medium: 'textSizeMedium', large: 'textSizeLarge' } as const;
const DENSITY_KEYS = { comfortable: 'densityComfortable', compact: 'densityCompact' } as const;
const ACCENT_KEYS: Record<AccentName, string> = {
  default: 'accentDefault',
  blue: 'accentBlue',
  green: 'accentGreen',
  purple: 'accentPurple',
  orange: 'accentOrange',
  rose: 'accentRose',
};

/** POS-I226: Settings > General > Appearance. Choices are saved to the user's account. */
export function AppearanceCard() {
  const { t } = useTranslation('settings');
  const { theme } = useThemeStore();
  const { fontSize, density, accent, change, reset } = useAppearanceStore();

  const segment = (selected: boolean) =>
    `rounded-lg border-2 px-3 py-2 text-sm font-medium transition-colors ${
      selected ? 'border-primary bg-primary/10' : 'border-transparent bg-muted/40 hover:bg-muted/70'
    }`;

  return (
    <Card className="animate-fade-in-up stagger-4 hover:shadow-glow-sm transition-all duration-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Palette className="h-5 w-5" />
          {t('appearanceTitle')}
        </CardTitle>
        <p className="text-sm text-muted-foreground">{t('appearanceNote')}</p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label>{t('appearanceTheme')}</Label>
          <div className="grid grid-cols-3 gap-3">
            {(['light', 'dark', 'system'] as ThemeChoice[]).map((key) => {
              const Icon = THEME_ICONS[key];
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={theme === key}
                  onClick={() => change({ theme: key })}
                  className={`flex flex-col items-center gap-2 rounded-xl border-2 p-4 transition-colors ${
                    theme === key ? 'border-primary bg-primary/10' : 'border-transparent hover:bg-muted/30'
                  }`}
                >
                  <Icon className="h-6 w-6" />
                  <span className="text-xs font-medium">{t(THEME_KEYS[key])}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('appearanceTextSize')}</Label>
          <div className="flex flex-wrap gap-2" role="group" aria-label={t('appearanceTextSize')}>
            {FONT_SIZES.map((key) => (
              <button key={key} type="button" aria-pressed={fontSize === key} onClick={() => change({ fontSize: key })} className={segment(fontSize === key)}>
                {t(FONT_KEYS[key])}
              </button>
            ))}
          </div>
          <p className="text-muted-foreground">{t('textSizeSample')}</p>
        </div>

        <div className="space-y-2">
          <Label>{t('appearanceDensity')}</Label>
          <div className="flex flex-wrap gap-2" role="group" aria-label={t('appearanceDensity')}>
            {DENSITIES.map((key) => (
              <button key={key} type="button" aria-pressed={density === key} onClick={() => change({ density: key })} className={segment(density === key)}>
                {t(DENSITY_KEYS[key])}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('appearanceColour')}</Label>
          <div className="flex flex-wrap gap-3" role="group" aria-label={t('appearanceColour')}>
            {ACCENTS.map((key) => (
              <button
                key={key}
                type="button"
                aria-label={t(ACCENT_KEYS[key])}
                aria-pressed={accent === key}
                title={t(ACCENT_KEYS[key])}
                onClick={() => change({ accent: key })}
                className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  accent === key ? 'border-foreground ring-2 ring-offset-2 ring-foreground/40' : 'border-border'
                }`}
                style={{ backgroundColor: `hsl(${ACCENT_SWATCH[key]})` }}
              >
                {accent === key && <Check className="h-4 w-4 text-white" aria-hidden="true" />}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('appearanceLanguage')}</Label>
          <div><LanguageSwitcher /></div>
          <p className="text-xs text-muted-foreground">{t('appearanceLanguageNote')}</p>
        </div>

        <div className="border-t pt-4">
          <Button type="button" variant="ghost" size="sm" onClick={reset}>{t('appearanceReset')}</Button>
        </div>
      </CardContent>
    </Card>
  );
}
